import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { isArrayEmpty } from "../../../libs/common";

import {
  SpaceTempHumidInfo,
  TAG_TYPE,
  TagInfo,
  TempHumidInfo,
  getMonitoringTempHumid,
} from "../../../services/iotServices";

import { useUserContext } from "../../../contexts/userContext";

import useServiceGroupBuilding from "../../../hooks/useServiceGroupBuilding";
import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import StickyHeader from "../../../components/Layout/StickyHeader";
import CommonCheckbox from "../../../components/Input/Checkbox";
import HorizontalRule from "../../../components/Rule/Horizontal";

import { NoDataIcon } from "../../Center";

type Props = {};

export default function ManagerTempHumid(props: Props) {
  const [user] = useUserContext();
  const [isLoading, setIsLoading] = React.useState(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const navigate = useNavigate();
  const [tempHumid, setTempHumid] = React.useState<TempHumidInfo>();
  const [isShowNoSensor, setIsShowNoSensor] = React.useState(false);
  const [selectedBuilding, selectBuildingComponent] = useServiceGroupBuilding({
    categoryCode: "MONITORING",
  });

  const loadTempHumid = async () => {
    setIsLoading(true);
    try {
      const result = await getMonitoringTempHumid({
        buildingId: selectedBuilding?.buildingId ?? undefined,
      });
      if (result.status === HttpStatusCode.Ok) {
        setTempHumid(result.data[0]);
      }
    } catch (e: any) {
      console.log("getMonitoringTempHumid", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user.isLoading === false && selectedBuilding?.buildingId) {
      loadTempHumid();
    }
  }, [user.isLoading, selectedBuilding?.buildingId]);

  return (
    <div className="px-5 flex flex-col gap-5">
      {loadingComponent}
      <StickyHeader
        title="온습도 모니터링"
        onBackClick={() => navigate("/view")}
        rightContent={
          <div onClick={() => loadTempHumid()}>
            <ManagerTempHumidRefreshIcon />
          </div>
        }
      />

      <div>
        <div className="pb-[30px]">{selectBuildingComponent}</div>
        <HorizontalRule />
      </div>

      <div className="pb-[30px] flex flex-col gap-5">
        <div className="flex items-center justify-between gap-2.5">
          <p className="flex-1 py-2 font-medium text-lg">층 별 온습도 오염도</p>
        </div>
        {tempHumid?.floors?.filter((floor) => floor.spaces.length > 0)
          ?.length &&
        tempHumid?.floors?.filter((floor) => floor.spaces.length > 0)?.length >
          0 ? (
          <>
            <div className="flex items-center gap-2.5">
              <CommonCheckbox
                checked={isShowNoSensor}
                onClick={(value) => setIsShowNoSensor(value)}
              />
              센서 미설치 공간 보기
            </div>
            <div className="whitespace-pre-line text-center py-5 px-2.5 bg-brand-weather-badge-background text-sm text-brand-neutral-80">
              {"센서점검 확인 : \n2시간 동안 온/습도값 미수신 or 동일값 수신"}
            </div>
          </>
        ) : (
          <></>
        )}
      </div>

      <div>
        <div className="pb-5 flex items-center justify-end text-sm gap-[12px]">
          <div className="flex items-center gap-1">
            <div className="h-[14px] w-[14px] rounded-full bg-[#ec1e9f]"></div>
            온도
          </div>
          <div className="flex items-center gap-1">
            <div className="h-[14px] w-[14px] rounded-full bg-[#00CDC1]"></div>
            습도
          </div>
        </div>

        <HorizontalRule
          className="border-black !border-t-2 border-l-0 border-r-0 border-b-0"
          margin="0"
        />
        {tempHumid?.floors?.filter((floor) => floor.spaces.length > 0)
          ?.length &&
        tempHumid?.floors?.filter((floor) => floor.spaces.length > 0)?.length >
          0 ? (
          tempHumid?.floors
            .filter((floor) => floor.spaces.length > 0)
            .map((floor, index, floorList) => {
              if (
                !isShowNoSensor &&
                floor.spaces.every((space) => space.tag.length === 0)
              ) {
                return null;
              }

              return (
                <div key={floor.buildingFloorId} className="flex flex-col">
                  <div className="w-full flex gap-[15px]">
                    <div className="bg-[#fafafa] w-[130px] flex items-center justify-center text-lg font-semibold border-r border-[#f3f3f3]">
                      {floor.floorName}
                    </div>
                    <div className="flex flex-col w-full">
                      {floor.spaces
                        .filter((item) => !isArrayEmpty(item.tag))
                        .map((space, index, spaceList) => {
                          // 센서가 없을때도 출력하는 설정이 켜져있거나 태그 데이터가 있는 경우만 출력한다.
                          if (isShowNoSensor === true || space.tag.length > 0) {
                            return (
                              <ManagerTempSpaceRow
                                key={space.buildingSpaceId}
                                space={space}
                                spaceList={spaceList}
                              />
                            );
                          } else {
                            return null;
                          }
                        })}
                    </div>
                  </div>
                  {index < (floorList?.length ?? 0) - 1 && (
                    <HorizontalRule margin="0" />
                  )}
                </div>
              );
            })
        ) : (
          <div className="sticky top-[72px] w-full h-40 flex flex-col items-center justify-center gap-2">
            <NoDataIcon />
            센서 데이터가 없어요
          </div>
        )}
        <HorizontalRule
          className="border-black !border-t-2 border-l-0 border-r-0 border-b-0"
          margin="0"
        />
      </div>
    </div>
  );
}

export type ManagerTempSpaceRowProps = {
  space: SpaceTempHumidInfo;
  spaceList: SpaceTempHumidInfo[];
};

const getTempHumidBattery = (battery?: TagInfo) => {
  let result = "0";

  if (battery === undefined || battery?.value === undefined) {
    return result + "%";
  }

  let value = Number((Number(battery.value) * 1000).toFixed(0));

  if (value > 3228) result = "100";
  else if (value >= 3122 && value <= 3227) result = "96";
  else if (value >= 3008 && value <= 3121) result = "92";
  else if (value >= 2972 && value <= 3007) result = "88";
  else if (value >= 2936 && value <= 2971) result = "83";
  else if (value >= 2900 && value <= 2935) result = "79";
  else if (value >= 2866 && value <= 2899) result = "75";
  else if (value >= 2848 && value <= 2865) result = "71";
  else if (value >= 2820 && value <= 2847) result = "67";
  else if (value >= 2792 && value <= 2819) result = "63";
  else if (value >= 2762 && value <= 2791) result = "54";
  else if (value >= 2724 && value <= 2761) result = "50";
  else if (value >= 2688 && value <= 2723) result = "46";
  else if (value >= 2678 && value <= 2687) result = "42";
  else if (value >= 2662 && value <= 2677) result = "38";
  else if (value >= 2646 && value <= 2661) result = "33";
  else if (value >= 2632 && value <= 2645) result = "29";
  else if (value >= 2600 && value <= 2631) result = "25";
  else if (value >= 2572 && value <= 2599) result = "21";
  else if (value >= 2554 && value <= 2571) result = "17";
  else if (value >= 2488 && value <= 2553) result = "13";
  else if (value >= 2422 && value <= 2487) result = "8";
  else if (value >= 2356 && value <= 2421) result = "4";
  else if (value >= 2200 && value <= 2355) result = "1";
  else result = "0";

  return result + battery.tagTypeUnit;
};

export const ManagerTempSpaceRow = ({
  space,
  spaceList,
}: ManagerTempSpaceRowProps) => {
  const battery = space.tag.find((tag) => tag.tagTypeId === TAG_TYPE.BATTERY);
  const temperature = space.tag.find(
    (tag) => tag.tagTypeId === TAG_TYPE.TEMPERATURE
  );
  const humidity = space.tag.find((tag) => tag.tagTypeId === TAG_TYPE.HUMIDITY);
  const isSensor = space.tag.length > 0;
  const index = spaceList.findIndex(
    (item) => item.buildingSpaceId === space.buildingSpaceId
  );

  const isSensorError = !temperature?.value && !humidity?.value;

  return (
    <>
      <div className="py-[15px] flex items-center gap-2.5 justify-between">
        <div>
          <div className="text-sm font-medium">{space.spaceName}</div>
          {isSensor && battery?.value && (
            <div className="text-xs">
              배터리 <span>{getTempHumidBattery(battery)}</span>
            </div>
          )}
        </div>
        {isSensor ? (
          <>
            {isSensorError ? (
              <div className="py-[5px] px-5 rounded-full bg-brand-primary-red text-white text-sm font-medium text-center min-w-[120px]">
                센서 이상 확인
              </div>
            ) : (
              <div className="flex items-center gap-2.5 text-sm font-semibold">
                {temperature && temperature?.value && (
                  <div className="text-[#EC1E9F] flex items-center gap-[3px]">
                    <span>{temperature?.value}</span>
                    <span>{temperature?.tagTypeUnit}</span>
                  </div>
                )}
                {humidity && humidity?.value && (
                  <div className="text-[#00CDC1] flex items-center gap-[3px]">
                    <span>{humidity?.value}</span>
                    <span>{humidity?.tagTypeUnit}</span>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="py-[5px] px-5 rounded-full bg-brand-main-page-background text-brand-neutral-50 text-sm font-medium text-center min-w-[120px]">
            센서 미설치
          </div>
        )}
        {/* TODO: 센서 이상 확인 적용 필요 */}
        {/* <div className="py-[5px] px-5 rounded-full bg-brand-primary-red text-white text-sm font-semibold text-center w-[120px]">
          센서 이상 확인
        </div> */}
      </div>
      {index < spaceList.length - 1 && (
        <HorizontalRule
          className="border-t border-l-0 border-r-0 border-b-0 border-[#f3f3f3]"
          margin="0"
        />
      )}
    </>
  );
};

export const ManagerTempHumidRefreshIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.85331 8.77173L4.50015 11.6241C4.52542 11.6679 4.58137 11.6829 4.62513 11.6576L7.47754 10.0108"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
    <path
      d="M4.68262 11.6742C4.79316 8.94731 6.25602 6.33445 8.79433 4.86895C12.7327 2.59514 17.7686 3.94452 20.0425 7.88288C22.3163 11.8212 20.9669 16.8572 17.0285 19.131C14.3437 20.6811 11.1487 20.5474 8.66652 19.0574C7.50764 18.3618 6.50412 17.3706 5.78041 16.1171"
      stroke="black"
      strokeWidth="1.75"
    />
  </svg>
);
